/**
 * @date: 2023-04-19
 * @author: Kevin Hsiau <khsiau@max.md>
 * @copy: (C) Copyright 2022 MaxMD Corporation, All Rights Reserved.
 */

import React, {useEffect, useRef} from 'react';
import {Alert, CircularProgress, Grid, styled, Tooltip} from "@mui/material";
import CurrentSyncPie from "./CurrentSyncPie";
import CurrentSyncBar from "./CurrentSyncBar";
import {dataOrganizer} from "./LastPublishChartsWidget";
import {convertDateToTimeString} from "../utils/cert-helper";
import moment from "moment";
import {useDispatch, useSelector} from "react-redux";
import {getFhirInformation} from "../features/currentDirectoryFhirInformationSlice";
import ShowMessage from "../utils/show-message";
import InfoIcon from "@mui/icons-material/Info";

const StyledLoadingAlert = styled(Alert)({
    '& .MuiAlert-message': {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
    },
})
const StyledSummaryDiv = styled('div') ({
    color: '#999999',
    '& div': {
        lineHeight: '30px',
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        '& strong': {
            color: '#337AB799', marginLeft: 5
        }
    }
})

// The two Endpoint labels have to name which kind of Endpoint they count, so they are long enough
// to wrap at the column's old width. Kept on one line here and the column widened to suit —
// a label broken across two lines reads as two separate rows next to a single value.
const SummaryLabel = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    whiteSpace: 'nowrap',
});

const summaryDescriptions = {
    hispCount: 'Count of HISPs in the most recent published CSV snapshot.',
    OrganizationAffiliation: 'Active FHIR OrganizationAffiliation resources. Built from mailing rows and linked to the HISP and organization.',
    Organization: 'Active FHIR Organization resources. Built from mailing rows, keyed by HISP plus ORG_UID.',
    Location: 'Active FHIR Location resources. Built only from practice rows and keyed by HISP, ORG_UID, and practice address.',
    PractitionerRole: 'Active FHIR PractitionerRole resources. Built from PROV_UID rows and keyed by HISP, ORG_UID, and PROV_UID.',
    Practitioner: 'Active FHIR Practitioner resources built from provider rows.',
    Endpoint: 'Active FHIR Endpoint resources with connectionType direct-project — the Direct addresses themselves, built from SERV_DIRECT_ADDR and keyed by HISP plus organization/provider context, not only the raw direct address. Capability pages are excluded and counted on their own line.',
    EndpointCapabilityPage: 'Active FHIR Endpoint resources with connectionType capability-page. Built from LOC_INFO — one per url — and reachable only through the Location\'s locInfoEX extension. These are web pages, not Direct addresses.',
    date: 'Current timestamp when this dashboard view was loaded.',
};

function SummaryRow({label, value, tooltip}) {
    return (
        <div>
            <SummaryLabel>
                {label}
                <Tooltip title={tooltip} arrow placement='top'>
                    <InfoIcon fontSize='small' sx={{fontSize: 16}} />
                </Tooltip>
            </SummaryLabel>
            <strong>{value}</strong>
        </div>
    );
}

export default function CurrentSyncWidget() {

    const dispatch = useDispatch();
    const {
        fhirInformationLoading,
        fhirEndpointResources,
        top3HispsCountBar,
        fhirSummary,
        fhirInformationMsg,
    } = useSelector(state => state.currentDirectFhirInformation);


    useEffect(() => {

        dispatch(getFhirInformation());

    }, [])



    console.log('[CurrentSyncWidget] loading:', fhirInformationLoading, '| fhirEndpointResources:', fhirEndpointResources, '| top3HispsCountBar:', top3HispsCountBar, '| fhirSummary:', fhirSummary);

    return (
        <>
            {!fhirInformationLoading && fhirEndpointResources && top3HispsCountBar && fhirSummary &&
                <RenderCurrentSyncWidget pie={fhirEndpointResources} bar={top3HispsCountBar} summary={fhirSummary}/>
            }
            {fhirInformationLoading &&
                <StyledLoadingAlert
                    severity='info'
                    icon={false}
                >
                    <CircularProgress size={20}/>
                    <span> Loading...</span>
                </StyledLoadingAlert>
            }
            { fhirInformationMsg && <ShowMessage msg={fhirInformationMsg} />}
        </>
    )

}

export function RenderCurrentSyncWidget(props) {
    const {
        pie,
        bar,
        summary
    } = props;

    const othersRef = useRef();
    const organize = () => {
        return dataOrganizer(pie, othersRef);
    }

    return (

        <Grid container style={{height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 16}}>
            <Grid item xs={12} md={3} lg={3}>
                {summary &&
                    <StyledSummaryDiv>
                        <SummaryRow label='HISPS:' value={summary.hispCount} tooltip={summaryDescriptions.hispCount} />
                        <SummaryRow label='Organization Affiliation:' value={summary.OrganizationAffiliation} tooltip={summaryDescriptions.OrganizationAffiliation} />
                        <SummaryRow label='Organization:' value={summary.Organization} tooltip={summaryDescriptions.Organization} />
                        <SummaryRow label='Location:' value={summary.Location} tooltip={summaryDescriptions.Location} />
                        <SummaryRow label='Practitioner Role:' value={summary.PractitionerRole} tooltip={summaryDescriptions.PractitionerRole} />
                        <SummaryRow label='Practitioner:' value={summary.Practitioner} tooltip={summaryDescriptions.Practitioner} />
                        <SummaryRow label='Endpoint (Direct Address):' value={summary.Endpoint} tooltip={summaryDescriptions.Endpoint} />
                        <SummaryRow label='Endpoint (Capability Page):' value={summary.EndpointCapabilityPage} tooltip={summaryDescriptions.EndpointCapabilityPage} />
                        <SummaryRow label='Date:' value={convertDateToTimeString(moment())} tooltip={summaryDescriptions.date} />
                    </StyledSummaryDiv>
                }
            </Grid>
            <Grid item xs={12} md={5} lg={5}>
                <CurrentSyncPie data={organize()} others={othersRef.current}/>
            </Grid>
            <Grid item xs={12} md={4} lg={4} align='center'>
                <CurrentSyncBar data={bar}/>
            </Grid>

        </Grid>
    )

}

